import {
  animate,
  animateChild,
  group,
  query,
  stagger,
  style,
  transition,
  trigger
} from '@angular/animations';

/**
 * Spring-based open animation for overlay menus (e.g. menu-overflow).
 * Anchors the transform-origin to the trigger corner and slides in
 * from the correct direction.
 *
 * Two animations run in parallel via group():
 *  - Fade: 0.12s ease-out — the element appears quickly
 *  - Spring transform: 0.22s spring cubic-bezier — scale + translate settle naturally
 *
 * Params:
 *  - translateY: '-6px' (Down) or '6px' (Up)
 *  - transformOrigin: e.g. 'top right', 'bottom left'
 *
 * Usage: [@menuOverflowAnimation]="menuAnimationParams"
 * where menuAnimationParams = { value: true, params: { translateY, transformOrigin } }
 */
export const menuOverflowAnimation = trigger('menuOverflowAnimation', [
  transition(
    ':enter',
    [
      style({
        opacity: 0,
        transform: 'scale(0.9) translateY({{translateY}})',
        transformOrigin: '{{transformOrigin}}'
      }),
      group([
        animate('0.12s ease-out', style({ opacity: 1 })),
        animate(
          '0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
          style({ transform: 'scale(1) translateY(0)' })
        )
      ])
    ],
    { params: { translateY: '-6px', transformOrigin: 'top right' } }
  )
]);

/**
 * Simple fade in/out.
 *
 * Usage: [@fadeAnimation]
 */
export const fadeAnimation = trigger('fadeAnimation', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('300ms ease-out', style({ opacity: 1 }))
  ]),
  transition(':leave', [
    style({ opacity: 1 }),
    animate('300ms ease-in', style({ opacity: 0 }))
  ])
]);

/**
 * Overlay fade used for dialog/bottom-sheet backdrops.
 *
 * The key difference from fadeAnimation: the :leave transition calls
 * animateChild() so that inner containers (slide-up/slide-down triggers)
 * are NOT blocked by the parent leave animation.
 *
 * Without this, Angular's animation engine silences all child :leave
 * animations when the parent has its own :leave — causing the
 * container to vanish without sliding.
 *
 * Usage: @overlayFadeAnimation on the overlay wrapper div.
 */
export const overlayFadeAnimation = trigger('overlayFadeAnimation', [
  transition(':enter', [
    style({ opacity: 0 }),
    group([
      query('@*', animateChild(), { optional: true }),
      animate('220ms ease-out', style({ opacity: 1 }))
    ])
  ]),
  transition(':leave', [
    group([
      query('@*', animateChild(), { optional: true }),
      animate('280ms ease-in', style({ opacity: 0 }))
    ])
  ])
]);

/**
 * Applied to the HOST element of overlay components (umss-dialog, umss-bottom-sheet).
 * When Angular destroys the component via a parent @if — regardless of what triggered
 * the state change — this :leave transition delays destruction by delegating to the
 * inner overlay's existing animation via animateChild().
 *
 * This means footer buttons, store updates, routing changes, etc. all play the
 * close animation automatically without any code changes in consuming components.
 *
 * The prerequisite is that [open] and the @if guard in the parent are tied to the
 * same variable, which is the standard usage pattern.
 *
 * Usage: add to the component's `animations` array; the base directive provides
 * the @HostBinding('@overlayHostLeave') automatically.
 */
export const overlayHostLeaveAnimation = trigger('overlayHostLeave', [
  transition(':leave', [
    group([
      // Direct case: this component owns the @overlayFadeAnimation overlay div
      // (umss-dialog / umss-bottom-sheet used without a wrapper)
      query('@overlayFadeAnimation', animateChild(), { optional: true }),
      // Wrapper case: this component contains a child with @overlayHostLeave
      // (e.g. lib-add-bank-account-dialog that renders <umss-dialog> inside).
      // Angular's query is view-scoped, so @overlayFadeAnimation can't be found
      // across the child component view boundary. Instead we cascade to the child's
      // own host :leave, which CAN reach @overlayFadeAnimation in its own view.
      query('@overlayHostLeave', animateChild(), { optional: true })
    ])
  ])
]);

/**
 * Fade in only — no leave animation.
 * Use inside conditional blocks (@if/@else) to avoid both the leaving
 * and entering content coexisting in the DOM during the leave transition.
 *
 * Usage: @fadeIn
 */
export const fadeIn = trigger('fadeIn', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('250ms ease-out', style({ opacity: 1 }))
  ])
]);

/**
 * Fade in only but with a delay.
 * Helpful for replacing lists/content where the leaving element
 * needs time to animate out before the empty placeholder fades in.
 *
 * Usage: @fadeInDelayed
 */
export const fadeInDelayed = trigger('fadeInDelayed', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('250ms 350ms ease-out', style({ opacity: 1 }))
  ])
]);

/**
 * Expands width from 0 to natural width on enter,
 * collapses back to 0 on leave. Fades simultaneously.
 * Ideal for chips/tags in horizontal flex containers.
 *
 * Usage: @horizontalCollapseInOut
 */
export const horizontalCollapseInOut = trigger('horizontalCollapseInOut', [
  transition(':enter', [
    style({
      opacity: 0,
      width: 0,
      minWidth: 0,
      paddingLeft: 0,
      paddingRight: 0,
      marginLeft: 0,
      marginRight: 0,
      borderWidth: 0,
      overflow: 'hidden',
      whiteSpace: 'nowrap'
    }),
    group([
      animate(
        '250ms cubic-bezier(0.35, 0, 0.25, 1)',
        style({
          width: '*',
          minWidth: '*',
          paddingLeft: '*',
          paddingRight: '*',
          marginLeft: '*',
          marginRight: '*',
          borderWidth: '*'
        })
      ),
      animate('250ms ease-out', style({ opacity: 1 }))
    ])
  ]),
  transition(':leave', [
    style({ overflow: 'hidden', whiteSpace: 'nowrap' }),
    group([
      animate('200ms ease-in', style({ opacity: 0 })),
      animate(
        '250ms cubic-bezier(0.35, 0, 0.25, 1)',
        style({
          width: 0,
          minWidth: 0,
          paddingLeft: 0,
          paddingRight: 0,
          marginLeft: 0,
          marginRight: 0,
          borderWidth: 0
        })
      )
    ])
  ])
]);

/**
 * Expands height from 0 to natural height on enter,
 * collapses back to 0 on leave. Fades simultaneously.
 * Ideal for collapsible sections, conditional form blocks, etc.
 *
 * Also safely animates padding and margins to 0. For gap-aware usage
 * inside flex containers, apply the `smoothCollapse` directive, which
 * sets `--smooth-collapse-gap` automatically.
 *
 * Usage (basic):      @collapseInOut
 * Usage (gap-aware):  <div smoothCollapse>
 */
export const collapseInOut = trigger('collapseInOut', [
  transition(':enter', [
    style({
      opacity: 0,
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
      marginTop: 0,
      marginBottom: 'var(--smooth-collapse-gap, 0px)',
      overflow: 'hidden'
    }),
    animate(
      '200ms cubic-bezier(0.35, 0, 0.25, 1)',
      style({
        height: '*',
        paddingTop: '*',
        paddingBottom: '*',
        marginTop: '*',
        marginBottom: '*'
      })
    ),
    animate('150ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1 }))
  ]),
  transition(':leave', [
    style({ opacity: 1, overflow: 'hidden' }),
    animate('150ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 0 })),
    animate(
      '200ms cubic-bezier(0.35, 0, 0.25, 1)',
      style({
        height: 0,
        paddingTop: 0,
        paddingBottom: 0,
        marginTop: 0,
        marginBottom: 'var(--smooth-collapse-gap, 0px)'
      })
    )
  ])
]);

/**
 * Slides in from the right on enter, slides out to the left on leave.
 * For steppers, wizards, or carousel-style navigation going forward.
 *
 * Usage: [@slideLeft]
 */
export const slideLeft = trigger('slideLeft', [
  transition(':enter', [
    style({ transform: 'translateX(100%)', opacity: 0 }),
    animate(
      '300ms cubic-bezier(0.35, 0, 0.25, 1)',
      style({ transform: 'translateX(0%)', opacity: 1 })
    )
  ]),
  transition(':leave', [
    style({ transform: 'translateX(0)', opacity: 1 }),
    animate(
      '300ms cubic-bezier(0.35, 0, 0.25, 1)',
      style({ transform: 'translateX(-100%)', opacity: 0 })
    )
  ])
]);

/**
 * Slides in from the left on enter, slides out to the right on leave.
 * For steppers, wizards, or carousel-style navigation going backward.
 *
 * Usage: [@slideRight]
 */
export const slideRight = trigger('slideRight', [
  transition(':enter', [
    style({ transform: 'translateX(-100%)', opacity: 0 }),
    animate(
      '300ms cubic-bezier(0.35, 0, 0.25, 1)',
      style({ transform: 'translateX(0%)', opacity: 1 })
    )
  ]),
  transition(':leave', [
    style({ transform: 'translateX(0)', opacity: 1 }),
    animate(
      '300ms cubic-bezier(0.35, 0, 0.25, 1)',
      style({ transform: 'translateX(100%)', opacity: 0 })
    )
  ])
]);

/**
 * Bi-directional horizontal slide for steppers/wizards.
 * Bind to a numeric step value — increments slide left, decrements slide right.
 *
 * Usage: [@slideStep]="currentStep"
 */
export const slideStep = trigger('slideStep', [
  transition(':increment', [
    query(':enter, :leave', style({ position: 'fixed', width: '100%' }), {
      optional: true
    }),
    group([
      query(
        ':enter',
        [
          style({ transform: 'translateX(100%)', opacity: 0 }),
          animate(
            '400ms cubic-bezier(0.35, 0, 0.25, 1)',
            style({ transform: 'translateX(0%)', opacity: 1 })
          )
        ],
        { optional: true }
      ),
      query(
        ':leave',
        [
          style({ transform: 'translateX(0)', opacity: 1 }),
          animate(
            '400ms cubic-bezier(0.35, 0, 0.25, 1)',
            style({ transform: 'translateX(-100%)', opacity: 0 })
          )
        ],
        { optional: true }
      )
    ])
  ]),
  transition(':decrement', [
    query(':enter, :leave', style({ position: 'fixed', width: '100%' }), {
      optional: true
    }),
    group([
      query(
        ':enter',
        [
          style({ transform: 'translateX(-100%)', opacity: 0 }),
          animate(
            '400ms cubic-bezier(0.35, 0, 0.25, 1)',
            style({ transform: 'translateX(0%)', opacity: 1 })
          )
        ],
        { optional: true }
      ),
      query(
        ':leave',
        [
          style({ transform: 'translateX(0)', opacity: 1 }),
          animate(
            '400ms cubic-bezier(0.35, 0, 0.25, 1)',
            style({ transform: 'translateX(100%)', opacity: 0 })
          )
        ],
        { optional: true }
      )
    ])
  ])
]);

/**
 * Fades list items in with a stagger delay on enter,
 * fades them out on leave.
 *
 * Usage: [@listFade]="items.length" on the container.
 */
export const listFade = trigger('listFade', [
  transition('* <=> *', [
    query(
      ':enter',
      [
        style({ opacity: 0 }),
        stagger('60ms', animate('300ms ease-out', style({ opacity: 1 })))
      ],
      { optional: true }
    ),
    query(':leave', animate('200ms ease-in', style({ opacity: 0 })), {
      optional: true
    })
  ])
]);

/**
 * Bi-directional horizontal slide for dialog content steps.
 * Similar to slideStep but uses absolute positioning within a relative container,
 * making it suitable for use inside dialogs/modals.
 *
 * Wrap steps in a container with `position: relative; overflow: hidden`,
 * and use `@if` with the step index to toggle between steps.
 *
 * Usage: [@dialogSlideStep]="currentStep" on the container.
 */
export const dialogSlideStep = trigger('dialogSlideStep', [
  transition(':increment', [
    style({ height: '!' }),
    query(
      ':leave',
      style({ position: 'absolute', top: 0, left: 0, width: '100%' }),
      {
        optional: true
      }
    ),
    query(':enter', style({ transform: 'translateX(100%)', opacity: 0 }), {
      optional: true
    }),
    group([
      animate('400ms cubic-bezier(0.35, 0, 0.25, 1)', style({ height: '*' })),
      query(
        ':leave',
        [
          animate(
            '400ms cubic-bezier(0.35, 0, 0.25, 1)',
            style({ transform: 'translateX(-100%)', opacity: 0 })
          )
        ],
        { optional: true }
      ),
      query(
        ':enter',
        [
          animate(
            '400ms cubic-bezier(0.35, 0, 0.25, 1)',
            style({ transform: 'translateX(0%)', opacity: 1 })
          )
        ],
        { optional: true }
      )
    ])
  ]),
  transition(':decrement', [
    style({ height: '!' }),
    query(
      ':leave',
      style({ position: 'absolute', top: 0, left: 0, width: '100%' }),
      {
        optional: true
      }
    ),
    query(':enter', style({ transform: 'translateX(-100%)', opacity: 0 }), {
      optional: true
    }),
    group([
      animate('400ms cubic-bezier(0.35, 0, 0.25, 1)', style({ height: '*' })),
      query(
        ':leave',
        [
          animate(
            '400ms cubic-bezier(0.35, 0, 0.25, 1)',
            style({ transform: 'translateX(100%)', opacity: 0 })
          )
        ],
        { optional: true }
      ),
      query(
        ':enter',
        [
          animate(
            '400ms cubic-bezier(0.35, 0, 0.25, 1)',
            style({ transform: 'translateX(0%)', opacity: 1 })
          )
        ],
        { optional: true }
      )
    ])
  ])
]);

/**
 * List items slide in from below and expand on enter,
 * slide out downward and collapse on leave.
 *
 * Usage: [@listSlideUp]="items.length" on the container.
 */
export const listSlideUp = trigger('listSlideUp', [
  transition('* <=> *', [
    query(
      ':enter',
      [
        style({ transform: 'translateY(20px)', opacity: 0, height: '0px' }),
        stagger('40ms', [
          animate('200ms ease-out', style({ height: '*' })),
          animate(
            '200ms ease-out',
            style({ transform: 'translateY(0%)', opacity: 1 })
          )
        ])
      ],
      { optional: true }
    ),
    query(
      ':leave',
      [
        animate(
          '150ms ease-in',
          style({ transform: 'translateY(20px)', opacity: 0 })
        ),
        animate('150ms ease-in', style({ height: '0px' }))
      ],
      { optional: true }
    )
  ])
]);

/**
 * Bi-directional horizontal slide for cards whose content is toggled by a keyed @for.
 * Increments slide new content in from the right, decrements from the left.
 *
 * Place on a container element. Inside, use a @for keyed by a unique value that
 * changes whenever you want to trigger the slide (e.g. a month key or date timestamp).
 * The @for must render exactly one child element (the "slide block").
 *
 * Usage: [@horizontalSlide]="animIndex" on the container.
 */
export const horizontalSlide = trigger('horizontalSlide', [
  transition(':increment', [
    style({ position: 'relative', overflow: 'hidden' }),
    query(
      '.slide-block:enter',
      [
        style({
          transform: 'translateX(100%)',
          opacity: 0,
          width: '100%',
          display: 'block'
        })
      ],
      { optional: true }
    ),
    query(
      '.slide-block:leave',
      [
        style({
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          display: 'block'
        })
      ],
      { optional: true }
    ),
    group([
      query(
        '.slide-block:leave',
        [
          group([
            animate('300ms ease-in', style({ opacity: 0 })),
            animate('300ms ease-out', style({ transform: 'translateX(-100%)' }))
          ])
        ],
        { optional: true }
      ),
      query(
        '.slide-block:enter',
        [
          group([
            animate('300ms ease-out', style({ opacity: 1 })),
            animate('300ms ease-out', style({ transform: 'translateX(0%)' }))
          ])
        ],
        { optional: true }
      )
    ])
  ]),
  transition(':decrement', [
    style({ position: 'relative', overflow: 'hidden' }),
    query(
      '.slide-block:enter',
      [
        style({
          transform: 'translateX(-100%)',
          opacity: 0,
          width: '100%',
          display: 'block'
        })
      ],
      { optional: true }
    ),
    query(
      '.slide-block:leave',
      [
        style({
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          display: 'block'
        })
      ],
      { optional: true }
    ),
    group([
      query(
        '.slide-block:leave',
        [
          group([
            animate('150ms ease-in', style({ opacity: 0 })),
            animate('300ms ease-out', style({ transform: 'translateX(100%)' }))
          ])
        ],
        { optional: true }
      ),
      query(
        '.slide-block:enter',
        [
          group([
            animate('150ms ease-out', style({ opacity: 1 })),
            animate('300ms ease-out', style({ transform: 'translateX(0%)' }))
          ])
        ],
        { optional: true }
      )
    ])
  ])
]);

/**
 * Dialog enter & leave animation — parametrized per type and position.
 *
 * For Modals (Center): scales up from 0.95 with a subtle translateY spring.
 *
 * Params:
 *  - enterFrom: initial transform state  (e.g. 'scale(0.95) translateY(10px)' or 'translateX(-100%)')
 *  - leaveTo:   final transform on leave (e.g. 'scale(0.95) translateY(6px)')
 *  - enterEasing: cubic-bezier for the enter transform ('cubic-bezier(0.34, 1.56, 0.64, 1)' for spring, '0.35, 0, 0.25, 1' for linear)
 *  - leaveEasing: cubic-bezier for the leave transform (defaults to 'ease-in')
 *  - enterFadeDuration: ms for the opacity fade-in (default 150)
 *  - enterSlideDuration: ms for the transform slide-in (default 200)
 *  - leaveFadeDuration: ms for the opacity fade-out (default 180)
 *  - leaveSlideDuration: ms for the transform slide-out (default 180)
 *  - leaveOpacity: final opacity for the container (default 0)
 *
 * Usage: [@dialogEnterLeave]="dialogAnimationParams"
 * where dialogAnimationParams = { value: true, params: { enterFrom, leaveTo, enterEasing, ... } }
 */
export const dialogEnterLeave = trigger('dialogEnterLeave', [
  transition(
    ':enter',
    [
      style({ opacity: 0, transform: '{{enterFrom}}' }),
      group([
        animate('{{enterFadeDuration}}ms ease-out', style({ opacity: 1 })),
        animate(
          '{{enterSlideDuration}}ms {{enterEasing}}',
          style({ transform: 'none' })
        )
      ])
    ],
    {
      params: {
        enterFrom: 'scale(0.95) translateY(10px)',
        enterEasing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
        enterFadeDuration: 150,
        enterSlideDuration: 200
      }
    }
  ),
  transition(
    ':leave',
    [
      group([
        animate(
          '{{leaveFadeDuration}}ms {{leaveEasing}}',
          style({ opacity: '{{leaveOpacity}}' })
        ),
        animate(
          '{{leaveSlideDuration}}ms {{leaveEasing}}',
          style({ transform: '{{leaveTo}}' })
        )
      ])
    ],
    {
      params: {
        leaveTo: 'scale(0.95) translateY(6px)',
        leaveEasing: 'ease-in',
        leaveFadeDuration: 180,
        leaveSlideDuration: 180,
        leaveOpacity: 0
      }
    }
  )
]);

/**
 * Bottom sheet slide up/down animation.
 *
 * Enter: slides up from the bottom of the screen with a spring easing.
 * Leave: slides back down and fades out.
 *
 * Usage: @bottomSheetAnimation on the sheet container.
 */
export const bottomSheetAnimation = trigger('bottomSheetAnimation', [
  transition(':enter', [
    style({ transform: 'translateY(100%)', opacity: 0 }),
    group([
      animate('150ms ease-out', style({ opacity: 1 })),
      animate(
        '320ms cubic-bezier(0.35, 0, 0.25, 1)',
        style({ transform: 'translateY(0)' })
      )
    ])
  ]),
  transition(':leave', [
    group([
      animate(
        '280ms cubic-bezier(0.35, 0, 0.25, 1)',
        style({ transform: 'translateY(100%)' })
      )
    ])
  ])
]);
